import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Typography, Progress, Space } from 'antd';
import { 
  HeartOutlined, 
  LoadingOutlined, 
  CheckOutlined
} from '@ant-design/icons';
import './AnalysisPage.css';

const { Content } = Layout;
const { Title, Text } = Typography;

const analysisSteps = [
  { text: '正在读取您的答题数据...', delay: 500 },
  { text: '分析四维度占有欲特征...', delay: 1500 },
  { text: '计算RPI指数和等级...', delay: 2800 },
  { text: '生成个性化建议和分析...', delay: 4200 },
  { text: '整理完整评估报告...', delay: 5600 }
];

const AnalysisPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  
  const [countdown, setCountdown] = useState(5);
  const [stepStates, setStepStates] = useState([]);

  const testTypeText = type === 'self' ? '给自己测' : '为恋人测';

  // 验证本地数据
  useEffect(() => {
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      navigate('/');
      return;
    }

    const answers = JSON.parse(localStorage.getItem(`rpi_answers_${type}_${sessionId}`) || 'null');
    if (!answers || answers.length !== 40) {
      console.log('没有完整的测试数据，跳转到问卷页');
      navigate(`/questionnaire/${type}`);
      return;
    }

    console.log('本地测试数据验证通过，准备生成报告...');
  }, [type, navigate]);

  // 倒计时并保存报告
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          
          // 保存报告到历史记录
          saveReportToHistory();
          
          navigate(`/report/${type}`, { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, type]);

  // 保存报告到历史记录
  const saveReportToHistory = () => {
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) return;

    const answers = JSON.parse(localStorage.getItem(`rpi_answers_${type}_${sessionId}`) || '[]');
    const demographic = JSON.parse(localStorage.getItem(`rpi_demographic_${type}_${sessionId}`) || '{}');
    
    if (answers.length !== 40) return;

    // 计算 RPI（简化版，完整计算在 ReportPage）
    const calculateSimpleRPI = (answers) => {
      const sum = answers.reduce((total, answer) => total + answer, 0);
      const mean = sum / answers.length;
      const rpiIndex = Math.round(mean * 20); // 简化计算
      
      // 计算四维度分数
      const dimensions = {
        control: { score: Math.round(answers.slice(0, 10).reduce((a, b) => a + b, 0) / 10 * 20) },
        jealousy: { score: Math.round(answers.slice(10, 20).reduce((a, b) => a + b, 0) / 10 * 20) },
        dependence: { score: Math.round(answers.slice(20, 30).reduce((a, b) => a + b, 0) / 10 * 20) },
        insecurity: { score: Math.round(answers.slice(30, 40).reduce((a, b) => a + b, 0) / 10 * 20) }
      };

      // 判断等级
      let level;
      if (rpiIndex < 20) level = '极低';
      else if (rpiIndex < 40) level = '较低';
      else if (rpiIndex < 60) level = '中等';
      else if (rpiIndex < 80) level = '较高';
      else level = '极高';

      return { rpiIndex, level, dimensions };
    };

    const result = calculateSimpleRPI(answers);
    
    // 创建报告对象
    const report = {
      id: Date.now().toString(),
      timestamp: new Date().toLocaleString('zh-CN'),
      rpiIndex: result.rpiIndex,
      level: result.level,
      dimensions: result.dimensions,
      demographic,
      answers
    };

    // 获取现有报告列表
    const key = `rpi_reports_${type}_${sessionId}`;
    const existingReports = JSON.parse(localStorage.getItem(key) || '[]');
    
    // 添加新报告到列表开头
    existingReports.unshift(report);
    
    // 最多保存20份报告
    if (existingReports.length > 20) {
      existingReports.pop();
    }
    
    // 保存到 localStorage
    localStorage.setItem(key, JSON.stringify(existingReports));
    
    // 保存当前报告ID，供 ReportPage 使用
    localStorage.setItem('currentReportId', report.id);
    
    console.log(`报告已保存: ${type} - ${report.id}`);
  };

  // 分析步骤动画
  useEffect(() => {
    setStepStates(analysisSteps.map(() => 'pending'));
    
    const timers = [];
    
    analysisSteps.forEach((step, index) => {
      const startTimer = setTimeout(() => {
        setStepStates(prev => {
          const newStates = [...prev];
          newStates[index] = 'loading';
          return newStates;
        });
        
        const completeTimer = setTimeout(() => {
          setStepStates(prev => {
            const newStates = [...prev];
            newStates[index] = 'completed';
            return newStates;
          });
        }, 1000);
        
        timers.push(completeTimer);
      }, step.delay);
      
      timers.push(startTimer);
    });
    
    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  return (
    <Layout className="rpi-analysis-layout">
      <Content className="rpi-analysis-content">
        {/* 主标题 */}
        <Space direction="vertical" align="center" size={16}>
          <div className="rpi-analysis-header">
            <div className="rpi-analysis-icon-wrapper">
              <HeartOutlined className="rpi-analysis-icon" />
            </div>
            <Title level={1} className="rpi-analysis-title">
              报告分析中{'.'.repeat(stepStates.filter(state => state !== 'pending').length)}
            </Title>
            <Text className="rpi-analysis-subtitle">
              RPI 恋爱占有欲指数测评 · {testTypeText}
            </Text>
          </div>

          {/* 进度圈 */}
          <div className="rpi-analysis-progress-circle">
            <Progress
              type="circle"
              percent={Math.round(((5 - countdown) / 5) * 100)}
              strokeColor={{
                '0%': '#F472B6',
                '50%': '#EC4899',
                '100%': '#BE185D'
              }}
              strokeWidth={8}
              width={180}
              format={() => (
                <div className="rpi-analysis-countdown">
                  <div className="rpi-analysis-countdown-number">{countdown}</div>
                  <div className="rpi-analysis-countdown-text">秒后查看报告</div>
                </div>
              )}
            />
          </div>

          {/* 分析步骤 */}
          <div className="rpi-analysis-steps">
            {analysisSteps.map((step, index) => {
              const state = stepStates[index] || 'pending';
              return (
                <div 
                  key={index} 
                  className={`rpi-analysis-step rpi-analysis-step-${state}`}
                >
                  <div className="rpi-analysis-step-icon">
                    {state === 'completed' && <CheckOutlined />}
                    {state === 'loading' && <LoadingOutlined spin />}
                    {state === 'pending' && <span className="rpi-analysis-step-dot"></span>}
                  </div>
                  <span className="rpi-analysis-step-text">{step.text}</span>
                </div>
              );
            })}
          </div>

          {/* 提示文字 */}
          <div className="rpi-analysis-tips">
            <Text className="rpi-analysis-tip-text">
              💡 我们正在基于心理学理论分析您的占有欲特征...
            </Text>
            <Text className="rpi-analysis-tip-text">
              📊 包含四维度分析、雷达图、人格类型、依恋模式等
            </Text>
          </div>
        </Space>
      </Content>
    </Layout>
  );
};

export default AnalysisPage;
